import { useDeferredValue, useState } from 'react';
import {
  DEFAULT_RESEARCH_MARKET_SORT,
  filterResearchMarkets,
  findResearchMarketBySlug,
  formatResearchMarketRulePercent,
  getResearchMarketLatestRuleRatio,
  sortResearchMarkets,
} from '../lib/researchMarkets';
import './ResearchMarketSwitcherSection.css';

function formatOptionLabel(market, rank) {
  return `#${rank} ${market.region_name} | ${formatResearchMarketRulePercent(getResearchMarketLatestRuleRatio(market))}`;
}

export default function ResearchMarketSwitcherSection({
  markets,
  activeRegionSlug,
  loading,
  error,
  switching = false,
  onSelectSlug,
}) {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const rankedMarkets = sortResearchMarkets(markets, DEFAULT_RESEARCH_MARKET_SORT);
  const activeMarket = findResearchMarketBySlug(rankedMarkets, activeRegionSlug);
  const filteredMarkets = sortResearchMarkets(
    filterResearchMarkets(rankedMarkets, deferredSearch),
    DEFAULT_RESEARCH_MARKET_SORT,
  );
  const activeRankIndex = rankedMarkets.findIndex((market) => market?.region_slug === activeRegionSlug);
  const previousMarket = activeRankIndex > 0 ? rankedMarkets[activeRankIndex - 1] : null;
  const nextMarket = activeRankIndex >= 0 && activeRankIndex < rankedMarkets.length - 1
    ? rankedMarkets[activeRankIndex + 1]
    : null;
  const visibleMarkets = activeMarket && !filteredMarkets.some((market) => market?.region_slug === activeMarket.region_slug)
    ? [activeMarket, ...filteredMarkets]
    : filteredMarkets;
  const disabled = loading || switching || !rankedMarkets.length;
  const noMatches = !loading && !error && deferredSearch.trim() && filteredMarkets.length === 0;

  function handleSelectChange(event) {
    const nextSlug = event.target.value;
    if (!nextSlug || nextSlug === activeRegionSlug || !onSelectSlug) return;
    onSelectSlug(nextSlug);
  }

  return (
    <section className="research-market-switcher">
      <div className="research-market-switcher__head">
        <p className="research-market-switcher__eyebrow">
          {switching ? 'Switching Market…' : 'Market Switcher'}
        </p>
      </div>

      <div className="research-market-switcher__controls">
        <label className="research-market-switcher__search">
          {/* <span className="research-market-switcher__label">Filter metros</span> */}
          <span className="research-market-switcher__search-field">
            <i className="pi pi-search" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Type a metro name"
              autoComplete="off"
            />
          </span>
        </label>

        <label className="research-market-switcher__select-wrap">
          {/* <span className="research-market-switcher__label">Jump to metro</span> */}
          <span className="research-market-switcher__select-shell">
            <select
              className="research-market-switcher__select"
              value={activeRegionSlug || ''}
              onChange={handleSelectChange}
              disabled={disabled}
              aria-label="Jump to metro market"
            >
              {!visibleMarkets.length ? (
                <option value="">
                  {loading ? 'Loading metro picks…' : 'No metro picks match this filter'}
                </option>
              ) : null}
              {visibleMarkets.map((market) => (
                <option key={market.region_slug} value={market.region_slug}>
                  {formatOptionLabel(market, rankedMarkets.findIndex((candidate) => candidate.region_slug === market.region_slug) + 1)}
                </option>
              ))}
            </select>
          </span>
        </label>

        <div className="research-market-switcher__nav" aria-label="Ranked market navigation">
          <button
            type="button"
            className="research-market-switcher__nav-button"
            onClick={() => previousMarket && onSelectSlug?.(previousMarket.region_slug)}
            disabled={disabled || !previousMarket}
            aria-label={previousMarket ? `Open previous ranked market: ${previousMarket.region_name}` : 'No previous ranked market'}
          >
            <i className="pi pi-arrow-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="research-market-switcher__nav-button"
            onClick={() => nextMarket && onSelectSlug?.(nextMarket.region_slug)}
            disabled={disabled || !nextMarket}
            aria-label={nextMarket ? `Open next ranked market: ${nextMarket.region_name}` : 'No next ranked market'}
          >
            <i className="pi pi-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </div>

      {error ? (
        <p className="research-market-switcher__state research-market-switcher__state--error">{error}</p>
      ) : noMatches ? (
        <p className="research-market-switcher__state">No metro picks matched that filter.</p>
      ) : loading ? (
        <p className="research-market-switcher__state">Loading the ranked metro list…</p>
      ) : null}
    </section>
  );
}
